import React, { useEffect, useMemo, useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion';

import { X } from 'lucide-react';



// Types

interface ItemBase { id: string; title: string; description?: string; thumbnail?: string; href?: string; }

interface ImageItem extends ItemBase { type: 'image'; full: string; }

interface VideoItem extends ItemBase { type: 'video'; videoUrl: string; }

type TabKey = 'images' | 'videos';



// Static Data

const tabs: { key: TabKey; label: string }[] = [{ key: 'images', label: 'Images' }, { key: 'videos', label: 'Videos' }];

const sampleImages: ImageItem[] = [];

const sampleVideos: VideoItem[] = [];

const tabsDataStatic: Record<TabKey, ItemBase[]> = { images: sampleImages, videos: sampleVideos };



// Framer Motion Variants

const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.08 } } };

const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } };



// Main Component

const Generations: React.FC = () => {

  const [active, setActive] = useState<TabKey>('videos');

  const [lightbox, setLightbox] = useState<ImageItem | VideoItem | null>(null);

  const [dynImages, setDynImages] = useState<ImageItem[] | null>(null);

  const [dynVideos, setDynVideos] = useState<VideoItem[] | null>(null);

  const [loading, setLoading] = useState(false);



  useEffect(() => {

    let cancelled = false;

    const loadManifest = async (path: string, setter: (data: any[]) => void) => {

      try {

        const res = await fetch(path, { cache: 'no-store' });

        if (res.ok) {

          const data = await res.json();

          if (!cancelled && Array.isArray(data)) setter(data);

        } else {

          setter([]);

        }

      } catch (_e) {

        setter([]);

      }

    };



    setLoading(true);

    Promise.all([

      loadManifest('/generations/images/manifest.json', (data) => setDynImages(data.filter(Boolean).map((d: any, i: number) => ({ id: d.id ?? `img-${i}`, type: 'image', title: d.title ?? 'Image', description: d.description ?? '', thumbnail: d.thumbnail ?? d.full, full: d.full ?? d.thumbnail })))),

      loadManifest('/generations/videos/manifest.json', (data) => setDynVideos(data.filter(Boolean).map((d: any, i: number) => ({ id: d.id ?? `vid-${i}`, type: 'video', title: d.title ?? 'Video', description: d.description ?? '', thumbnail: d.thumbnail, videoUrl: d.videoUrl }))))

    ]).finally(() => setLoading(false));



    return () => { cancelled = true; };

  }, []);



  const items = useMemo(() => {

    const source = active === 'images' && dynImages ? dynImages : active === 'videos' && dynVideos ? dynVideos : tabsDataStatic[active];

    return source as (ImageItem | VideoItem)[];

  }, [active, dynImages, dynVideos]);



  return (

    <div className="relative max-w-6xl mx-auto">

      <div className="flex items-center justify-center gap-8 mb-12 border-b border-[var(--color-border)]">

        {tabs.map(t => (

          <button

            key={t.key}

            className={`font-display text-2xl py-4 relative transition-colors duration-300 ${active === t.key ? 'text-[var(--color-foreground)]' : 'text-[var(--color-muted)] hover:text-[var(--color-foreground)]'}`}

            onClick={() => setActive(t.key)}

          >

            {t.label}

            {active === t.key && <motion.div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--color-foreground)]" layoutId="underline" />}

          </button>

        ))}

      </div>



      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">

        {items.length === 0 ? (

          <div className="col-span-full text-center text-[var(--color-muted)] py-24 border border-dashed border-[var(--color-border)] rounded-lg">

            {loading ? <p>Loading {active}...</p> : <p>No {active} found.</p>}

            <p className="text-sm opacity-70 mt-2">Add a manifest at <code className="px-1.5 py-1 bg-[var(--color-border)] rounded-md text-xs">/generations/{active}/manifest.json</code></p>

          </div>

        ) : items.map((item) => (

          <motion.div

            key={item.id}

            variants={itemVariants}

            className="group cursor-pointer"

            onClick={() => setLightbox(item)}

          >

            <div className="aspect-video overflow-hidden rounded-lg border border-[var(--color-border)] mb-4">

              {item.type === 'video' ? (

                <video

                  src={item.videoUrl}

                  className="w-full h-full object-cover"

                  autoPlay

                  loop

                  muted

                  playsInline

                  preload="metadata"

                />

              ) : (

                <img src={item.thumbnail || (item as ImageItem).full} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

              )}

            </div>

            <h4 className="font-semibold text-lg text-[var(--color-foreground)]">{item.title}</h4>

            {item.description && <p className="text-sm text-[var(--color-muted)] line-clamp-2">{item.description}</p>}

          </motion.div>

        ))}

      </motion.div>



      <AnimatePresence>

        {lightbox && (

          <motion.div

            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"

            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}

            onClick={() => setLightbox(null)}

          >

            <motion.div

              className="relative max-w-4xl max-h-[90vh] w-full"

              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}

              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}

              onClick={(e) => e.stopPropagation()}

            >

              <button className="absolute -top-12 right-0 text-gray-400 hover:text-white transition-colors" onClick={() => setLightbox(null)} aria-label="Close">

                <X size={32} />

              </button>

              {lightbox.type === 'image' ? (

                <img src={lightbox.full} alt={lightbox.title} className="max-h-[90vh] w-full h-auto object-contain rounded-lg" />

              ) : (

                <video src={lightbox.videoUrl} controls autoPlay playsInline className="max-h-[90vh] w-full h-auto object-contain rounded-lg" />

              )}

            </motion.div>

          </motion.div>

        )}

      </AnimatePresence>

    </div>

  );

};



export default Generations;

